/**
 * @file ProducerCertificatesList.tsx
 * @description Блок «Сертификаты» карточки производителя (phase19.31;
 * phase19.16, партия «Юридические документы», шаг В — SB141;
 * uiux-legal-steps-b-c §6.1, §6.2): «+ Сертификат», полоса о просроченных,
 * список со статусом на сайте (шесть состояний), «⋯» — «Изменить»,
 * «Открыть файл», «Удалить»; под списком — где документы видят покупатели
 * (ссылка на автоматическую группу или «Создать страницу»).
 *
 * На ПК — таблица «Документ / Срок / Файл / На сайте / ⋯», до 1024 —
 * компактные строки: название и номер, статус, срок и файл.
 */

import { FileCheck2, FileText, Pencil, Plus, Trash2 } from "lucide-react";

import { CertificatesWhereNote } from "./CertificatesWhereNote";
import { Button } from "../../ui/button";
import { NoticeStrip } from "../../ui/NoticeStrip";
import { ResponsiveList } from "../../ui/ResponsiveList";
import type { ListColumn } from "../../ui/responsive-list-types";
import { SectionCard } from "../../ui/SectionCard";
import { StatusPill } from "../../ui/StatusPill";
import type { ProducerCertificate } from "../../../lib/api-catalog";
import { CERT_LABELS, shortDate } from "../../../lib/catalog-format";
import { CERT_TONE_STATUS, certificateFileLabel, certificateStatus, hasCertFile } from "../../../lib/certificate-status";
import { plural } from "../../../lib/site-view/footer-columns";

interface Props {
  certs: ProducerCertificate[];
  producerActive: boolean;
  canEdit: boolean;
  onAdd: () => void;
  onEdit: (cert: ProducerCertificate) => void;
  onOpenFile: (cert: ProducerCertificate) => void;
  onRemove: (cert: ProducerCertificate) => void;
}

/** Срок словами: «до 12.10.2027» или «бессрочный». */
const termOf = (c: ProducerCertificate): string => (c.validUntil ? `до ${shortDate(c.validUntil)}` : "бессрочный");

export function ProducerCertificatesList({ certs, producerActive, canEdit, onAdd, onEdit, onOpenFile, onRemove }: Props) {
  const status = (c: ProducerCertificate) => certificateStatus(c, producerActive);
  const pill = (c: ProducerCertificate) => {
    const s = status(c);
    return <StatusPill tone={CERT_TONE_STATUS[s.tone]}>{s.label}</StatusPill>;
  };
  // Узкий экран: короткая подпись до « — » («Просрочен»), полная — в подсказке и для чтения экрана (QA 2026-10-20).
  const shortPill = (c: ProducerCertificate) => {
    const s = status(c);
    const [short, ...rest] = s.label.split(" — ");
    return (
      <span title={s.label} className="inline-flex max-w-full">
        <StatusPill tone={CERT_TONE_STATUS[s.tone]}>
          {short}
          {rest.length > 0 ? <span className="sr-only"> — {rest.join(" — ")}</span> : null}
        </StatusPill>
      </span>
    );
  };
  const columns: ListColumn<ProducerCertificate>[] = [
    {
      key: "title",
      header: "Документ",
      cell: (c) => (
        <span className="flex min-w-0 flex-col">
          <span className="break-words">{CERT_LABELS[c.type]}</span>
          <span className="type-caption break-all text-on-surface-variant">№ {c.number}</span>
        </span>
      ),
      compact: "title",
    },
    { key: "site", header: "На сайте", cell: pill, compactCell: shortPill, compact: "subtitle" },
    { key: "term", header: "Срок", cell: termOf, width: "w-[140px]", compact: "meta" },
    { key: "file", header: "Файл", cell: certificateFileLabel, width: "w-[140px]", compact: "meta" },
  ];
  const expired = certs.filter((c) => status(c).code === "expired").length;

  return (
    <SectionCard title="Сертификаты">
      {canEdit ? (
        <div className="flex justify-start">
          <Button variant="outline" onClick={onAdd}>
            <Plus className="size-4" />
            Сертификат
          </Button>
        </div>
      ) : null}
      {expired > 0 ? (
        <NoticeStrip tone="warning">
          {expired}{" "}
          {plural(expired, "документ просрочен и не показывается", "документа просрочены и не показываются", "документов просрочены и не показываются")} на
          сайте. Загрузите новый.
        </NoticeStrip>
      ) : null}
      <ResponsiveList
        aria-label="Сертификаты производителя"
        layout="compact"
        framed={false}
        rows={certs}
        columns={columns}
        rowKey={(c) => c.id}
        rowActions={(c) => [
          { key: "edit", label: canEdit ? "Изменить" : "Открыть", icon: Pencil, run: () => onEdit(c) },
          ...(hasCertFile(c) ? [{ key: "file", label: "Открыть файл", icon: FileText, run: () => onOpenFile(c) }] : []),
          ...(canEdit ? [{ key: "delete", label: "Удалить", icon: Trash2, danger: true, run: () => onRemove(c) }] : []),
        ]}
        rowActionsTitle={(c) => `Сертификат № ${c.number}`}
        empty={{ icon: FileCheck2, title: "Сертификатов пока нет", description: "Для БАД свидетельство о госрегистрации (СГР) обязательно" }}
      />
      <CertificatesWhereNote />
    </SectionCard>
  );
}
